/* eslint-disable no-nested-ternary */
import React from 'react'
import {useTranslation} from '@modifi/plugin-i18n'
import {times} from 'ramda'
import {Regular} from '@modifi/ui'

import {Table, TableHead, TableRow, TableData, TableWrapper, StyledButtonLink} from './styles'

const formatDataForPercentage = (data: React.ReactNode, headline: string): React.ReactNode =>
  headline.includes('Percentage') && (typeof data === 'string' || typeof data === 'number') && data
    ? `${data} %`
    : data

/** Columns are addressed by headline, so a cell can hold anything renderable. */
const getColumnValue = (entry: DynamicEntry | undefined, headline: string): React.ReactNode =>
  (entry?.[headline] ?? null) as React.ReactNode

export type DynamicEntry = {
  name?: string | null
  route?: string | null
  /**
   * Every summary page renders a different delete button with its own props, so the
   * button/props pair is intentionally untyped here and validated by the calling page.
   */
  deleteButton?: React.ComponentType<any> | null
  deleteProps?: Record<string, unknown> | null
  [column: string]: unknown
}

type DynamicEntriesTableProps = {
  headlines: string[]
  dataList: DynamicEntry[]
  transPrefix: string
  redirect?: (route?: string | null) => void
  onItemClick?: (item: DynamicEntry) => void
}

const DynamicEntriesTable: React.FC<DynamicEntriesTableProps> = ({
  headlines,
  dataList,
  transPrefix,
  redirect = () => null,
  onItemClick,
}) => {
  const {t} = useTranslation('charon')

  const handleItemClick = (item: DynamicEntry) => {
    if (onItemClick) onItemClick(item)
    else {
      redirect?.(item?.route)
    }
  }

  return (
    <TableWrapper>
      <Table>
        <thead>
          <TableRow>
            {headlines.map(headline => (
              <TableHead key={headline}>{t(`${transPrefix}.${headline}.table-headline`)}</TableHead>
            ))}
            <TableHead />
          </TableRow>
        </thead>

        <tbody>
          {times(index => {
            const DeleteButton = dataList[index]?.deleteButton
            return (
              <TableRow key={dataList[index]?.name}>
                {headlines?.map((headline, dataIndex) => (
                  <TableData key={headline}>
                    {dataIndex === 0 ? (
                      <StyledButtonLink
                        data-target={`${transPrefix}.details-page`}
                        transparent
                        onClick={() => handleItemClick(dataList?.[index])}
                      >
                        {getColumnValue(dataList?.[index], headline) || '-'}
                      </StyledButtonLink>
                    ) : (
                      <Regular>
                        {formatDataForPercentage(
                          getColumnValue(dataList?.[index], headline),
                          headline
                        ) || '-'}
                      </Regular>
                    )}
                  </TableData>
                ))}
                {!!DeleteButton && (
                  <TableData>
                    <DeleteButton {...dataList?.[index]?.deleteProps} />
                  </TableData>
                )}
              </TableRow>
            )
          }, dataList?.length)}
        </tbody>
      </Table>
    </TableWrapper>
  )
}

export default DynamicEntriesTable
